<script setup>
import { ref, computed } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import ProductRail from '@/Components/Public/ProductRail.vue'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  property:  { type: Object, required: true },
  base_path: { type: String, default: '/property' },
  related:   { type: Array, default: () => [] }
})

const activeIdx = ref(0)
const photos = props.property.photo_urls?.length ? props.property.photo_urls : []
const eyebrow = computed(() => {
  const parts = [t(`host_portal.property.categories.${props.property.category}`)]
  if (props.property.area) parts.push(props.property.area)
  return parts.join(' · ')
})
const price = computed(() => props.property.price_per_night || props.property.price_per_month)
</script>

<template>
  <Head>
    <title>{{ property.title }} · {{ t('public.head_title') }}</title>
    <meta name="description" :content="property.summary || property.description">
  </Head>

  <BrandHero kind="property" :eyebrow="eyebrow" :title="property.title">
    <template #top>
      <a :href="base_path" class="bhero-back">← {{ t('public.properties.back') }}</a>
    </template>
    <p v-if="property.summary" class="bhero-lede">{{ property.summary }}</p>
    <span v-if="property.verified" class="bhero-verified">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>{{ t('public.verified') }}
    </span>
    <p v-if="price" class="bhero-price"><span class="fw-value">{{ price }}</span></p>
    <div v-if="property.contact_intents && Object.keys(property.contact_intents).length" class="bhero-ctas">
      <a
        v-for="(url, platform, idx) in property.contact_intents"
        :key="platform"
        :class="['bhero-cta', idx === 0 ? 'bhero-cta--solid' : 'bhero-cta--ghost']"
        :href="url"
        target="_blank"
        rel="noopener"
      >{{ t('public.write_on', { platform: platform.charAt(0).toUpperCase() + platform.slice(1) }) }}</a>
    </div>
  </BrandHero>

  <section class="body">
    <div class="body__inner">
      <div class="layout">
        <div class="gallery">
          <div class="gallery__main">
            <img v-if="photos[activeIdx]" :src="photos[activeIdx]" :alt="property.title">
            <CatalogPlaceholder v-else kind="property" :category="property.category" />
          </div>
          <ul v-if="photos.length > 1" class="thumbs">
            <li v-for="(p, i) in photos" :key="p">
              <button :class="['thumb', i === activeIdx && 'is-active']" @click="activeIdx = i">
                <img :src="p" alt="">
              </button>
            </li>
          </ul>
        </div>

        <div class="info">
          <section v-if="property.description" class="card">
            <h2 class="section-title">{{ t('public.properties.sections.about') }}</h2>
            <p class="prose">{{ property.description }}</p>
          </section>

          <section class="card">
            <h2 class="section-title">{{ t('public.properties.sections.details') }}</h2>
            <dl class="dl">
              <div v-if="property.area"><dt>{{ t('public.properties.fields.area') }}</dt><dd>{{ property.area }}</dd></div>
              <div v-if="property.bedrooms"><dt>{{ t('public.properties.fields.bedrooms') }}</dt><dd>{{ property.bedrooms }}</dd></div>
              <div v-if="property.bathrooms"><dt>{{ t('public.properties.fields.bathrooms') }}</dt><dd>{{ property.bathrooms }}</dd></div>
              <div v-if="property.max_guests"><dt>{{ t('public.properties.fields.max_guests') }}</dt><dd>{{ property.max_guests }}</dd></div>
              <div v-if="property.size_sqm"><dt>{{ t('public.properties.fields.size_sqm') }}</dt><dd>{{ property.size_sqm }}</dd></div>
              <div v-if="property.price_per_night"><dt>{{ t('public.properties.fields.price_per_night') }}</dt><dd class="dl__price">{{ property.price_per_night }}</dd></div>
              <div v-if="property.price_per_month"><dt>{{ t('public.properties.fields.price_per_month') }}</dt><dd class="dl__price">{{ property.price_per_month }}</dd></div>
            </dl>
          </section>

          <section v-if="property.amenities && property.amenities.length" class="card">
            <h2 class="section-title">{{ t('public.properties.sections.amenities') }}</h2>
            <ul class="amenities">
              <li v-for="a in property.amenities" :key="a" class="amenity">{{ t(`host_portal.property.amenities.${a}`) }}</li>
            </ul>
          </section>

          <section v-if="property.blackouts && property.blackouts.length" class="card">
            <h2 class="section-title">{{ t('public.properties.sections.unavailable') }}</h2>
            <ul class="blackouts">
              <li v-for="(b, i) in property.blackouts" :key="i" class="blackout">{{ b.start_on }} → {{ b.end_on }}</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  </section>

  <section
    v-if="related.length"
    class="related"
  >
    <div class="related__inner">
      <ProductRail
        :title="t('public.properties.related')"
        kind="property"
        :items="related"
        :item-base="base_path"
        :view-all-href="base_path"
      />
    </div>
  </section>
</template>

<style scoped>
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-back:hover { opacity: 1; }
.bhero-lede { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 52ch; opacity: 0.94; }
.bhero-verified {
  display: inline-flex; align-items: center; gap: 0.35rem; margin: 1rem 0 0;
  padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700;
  background: color-mix(in oklch, currentColor 16%, transparent);
  border: 1px solid color-mix(in oklch, currentColor 35%, transparent);
}
.bhero-verified svg { width: 1rem; height: 1rem; }
.bhero-price { margin: 1.1rem 0 0; font-size: clamp(1.6rem, 3vw, 2.1rem); }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }
.bhero-cta {
  display: inline-flex; align-items: center; padding: 0.8rem 1.4rem; border-radius: 999px;
  font-family: var(--font-display, var(--display)); font-weight: 700; font-size: 1rem; text-decoration: none;
}
.bhero-cta--solid { background: var(--accent); color: var(--accentInk); }
.bhero-cta--ghost { background: color-mix(in oklch, var(--bg) 14%, transparent); color: inherit; border: 1px solid color-mix(in oklch, var(--bg) 45%, transparent); }

.body { background: var(--bg); }
.body__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; }
.layout { display: grid; grid-template-columns: 1fr; gap: 2rem; }
@media (min-width: 880px) { .layout { grid-template-columns: 1.4fr 1fr; gap: 2.5rem; } }
.gallery__main { aspect-ratio: 4 / 3; background: var(--surfaceAlt); border-radius: 1rem; overflow: hidden; display: grid; place-items: center; }
.gallery__main img { width: 100%; height: 100%; object-fit: cover; }
.thumbs { list-style: none; padding: 0; margin: 0.75rem 0 0; display: flex; gap: 0.5rem; overflow-x: auto; }
.thumb { width: 5rem; height: 4rem; padding: 0; border: 2px solid transparent; border-radius: 0.5rem; overflow: hidden; cursor: pointer; background: var(--surfaceAlt); }
.thumb.is-active { border-color: var(--accent); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.info { display: grid; gap: 1rem; }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.section-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }
.dl { display: grid; gap: 0.55rem; margin: 0; }
.dl > div { display: grid; grid-template-columns: 9rem 1fr; gap: 0.5rem; align-items: baseline; }
.dl dt { color: var(--muted); font-size: 0.875rem; }
.dl dd { margin: 0; color: var(--ink); font-size: 1rem; }
.dl__price { color: var(--accent); font-weight: 700; }
.amenities { list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 0.4rem; }
.amenity { padding: 0.3rem 0.75rem; border: 1px solid var(--line); border-radius: 999px; font-size: 0.875rem; color: var(--inkSoft); }
.blackouts { list-style: none; padding: 0; margin: 0; display: grid; gap: 0.3rem; }
.blackout { font-size: 0.9rem; color: var(--inkSoft); }
.related { background: var(--surface); border-top: 1px solid var(--line); }
.related__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem); }
</style>
